// === Task 9 === Inline 3-step Add-Interaction wizard substep.
// Ported from _editor/components/workflow/AddInteractionWizard.jsx with the
// Modal wrapper / visible-gating / close-X stripped (the parent WorkflowTab
// renders this conditionally and owns the back-to-graph control).
import React, { useState } from "react";
import i18n from "i18next";
import { connect } from "react-redux";
import { mergeWorkflowProposal } from "../../../../../common/actions";
import "./WizardSubstep.scss";

const INTERACTION_KINDS = [
  { kind: "MultipleChoice", label: { en: "Quiz", tr: "Sınav" }, icon: "⊜" },
  { kind: "FlipCard", label: { en: "Card flip", tr: "Kart çevir" }, icon: "🃏" },
  { kind: "YesNo", label: { en: "Yes / No", tr: "Evet / Hayır" }, icon: "✓" },
  { kind: "HotspotImages", label: { en: "Hotspot", tr: "Sıcak nokta" }, icon: "🎯" },
  { kind: "FillInBlank", label: { en: "Free input", tr: "Serbest giriş" }, icon: "📝" },
];

const OUTCOME_KINDS = [
  { id: "continue", label: { en: "Continue the scene", tr: "Sahneye devam et" } },
  { id: "branch", label: { en: "Branch (correct/wrong)", tr: "Dallan (doğru/yanlış)" } },
  { id: "feedback", label: { en: "Show feedback first", tr: "Önce geri bildirim göster" } },
  { id: "variable", label: { en: "Set a variable", tr: "Bir değişken ayarla" } },
];

function buildProposal({ kind, outcome, targetSceneId, pluginBoxId }) {
  const variables =
    outcome === "variable" || outcome === "branch"
      ? [{ id: "score", type: "number", initial: 0, scope: "session" }]
      : [];

  const triggers = [];
  const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 8)}`;

  if (outcome === "continue") {
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.correct",
      actions: [{ kind: "resume" }],
    });
  } else if (outcome === "branch") {
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.correct",
      actions: [{ kind: "setVar", varId: "score", op: "add", value: 10 }, { kind: "resume" }],
    });
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.wrong",
      actions: targetSceneId
        ? [{ kind: "jumpScene", target: targetSceneId }]
        : [{ kind: "replayItem", pluginBoxId }],
    });
  } else if (outcome === "feedback") {
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.correct",
      actions: [
        { kind: "showText", text: { en: "Right!", tr: "Doğru!" }, durationMs: 1200 },
        { kind: "resume" },
      ],
    });
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.wrong",
      actions: [
        { kind: "showText", text: { en: "Try again", tr: "Tekrar dene" }, durationMs: 1200 },
        { kind: "replayItem", pluginBoxId },
      ],
    });
  } else if (outcome === "variable") {
    triggers.push({
      id: uid("t"),
      sceneId: "*",
      pluginBoxId,
      on: "onAnswer.correct",
      actions: [{ kind: "setVar", varId: "score", op: "add", value: 10 }, { kind: "resume" }],
    });
  }

  return { variables, triggers, kind };
}

export function WizardSubstep({
  locale,
  pluginBoxId,
  sectionId,
  availableScenes,
  onBack,
  onApply,
}) {
  const [step, setStep] = useState(1);
  const [kind, setKind] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [targetSceneId, setTargetSceneId] = useState("");

  const tr = locale === "tr";

  const labelFor = (item) =>
    tr
      ? item.label && (item.label.tr || item.label.en)
      : item.label && (item.label.en || item.label.tr);

  const finish = () => {
    const proposal = buildProposal({ kind, outcome, targetSceneId, pluginBoxId });
    onApply(sectionId, proposal);
    onBack();
  };

  return (
    <div className="wizard-substep">
      <div className="wizard-substep__body">
        <div className="wizard-substep__head">
          <span>✨ {i18n.t("Workflow.add_interaction", { defaultValue: "Add Interaction" })}</span>
        </div>
        <div className="wizard-substep__step-indicator">
          {[1, 2, 3].map((n) => (
            <span key={n} className={step === n ? "active" : step > n ? "done" : ""}>
              {n}
            </span>
          ))}
        </div>

        {step === 1 && (
          <div className="wizard-substep__step">
            <div className="wizard-substep__q">
              {i18n.t("Wizard.q1", { defaultValue: "What kind of interaction?" })}
            </div>
            <div className="wizard-substep__grid">
              {INTERACTION_KINDS.map((k) => (
                <button
                  key={k.kind}
                  type="button"
                  className={
                    kind === k.kind ? "wizard-substep__card active" : "wizard-substep__card"
                  }
                  onClick={() => {
                    setKind(k.kind);
                    setStep(2);
                  }}
                >
                  <span className="wizard-substep__icon">{k.icon}</span>
                  <span>{labelFor(k)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-substep__step">
            <div className="wizard-substep__q">
              {i18n.t("Wizard.q2", {
                defaultValue: "When the learner does that, what happens?",
              })}
            </div>
            <div className="wizard-substep__list">
              {OUTCOME_KINDS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={
                    outcome === o.id ? "wizard-substep__option active" : "wizard-substep__option"
                  }
                  onClick={() => {
                    setOutcome(o.id);
                    setStep(3);
                  }}
                >
                  {labelFor(o)}
                </button>
              ))}
            </div>
            <div className="wizard-substep__nav">
              <button type="button" onClick={() => setStep(1)}>
                {i18n.t("Wizard.back", { defaultValue: "Back" })}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="wizard-substep__step">
            <div className="wizard-substep__q">
              {outcome === "branch"
                ? i18n.t("Wizard.q3_branch", {
                    defaultValue: "Where does the WRONG path lead?",
                  })
                : i18n.t("Wizard.q3_review", { defaultValue: "Review your choices" })}
            </div>
            {outcome === "branch" && (
              <select
                className="wizard-substep__select"
                value={targetSceneId}
                onChange={(e) => setTargetSceneId(e.target.value)}
              >
                <option value="">
                  {i18n.t("Wizard.pick_scene", { defaultValue: "Pick a scene…" })}
                </option>
                {(availableScenes || []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title || s.id}
                  </option>
                ))}
              </select>
            )}
            <div className="wizard-substep__summary">
              {kind} → {outcome}
              {outcome === "branch" && targetSceneId ? ` → ${targetSceneId}` : ""}
            </div>
            <div className="wizard-substep__nav">
              <button type="button" onClick={() => setStep(2)}>
                {i18n.t("Wizard.back", { defaultValue: "Back" })}
              </button>
              <button type="button" onClick={finish} className="wizard-substep__create">
                {i18n.t("Wizard.create", { defaultValue: "Create" })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  const present = (state.undoGroup && state.undoGroup.present) || state.undoGroup || {};
  const navItemsById = present.navItemsById || {};
  const sceneId = present.navItemSelected;
  const scene = sceneId ? navItemsById[sceneId] : null;
  const sectionId = scene ? scene.parent : null;
  const pluginBoxId =
    present.boxSelected && present.boxSelected !== -1 ? present.boxSelected : null;
  let availableScenes = sectionId
    ? Object.values(navItemsById)
        .filter((n) => n && n.parent === sectionId && n.type !== "section")
        .map((n) => ({ id: n.id, title: n.title || n.name || n.id }))
    : [];
  if (availableScenes.length === 0) {
    availableScenes = Object.values(navItemsById)
      .filter((n) => n && n.type !== "section")
      .map((n) => ({ id: n.id, title: n.title || n.name || n.id }));
  }
  const locale =
    (state.i18n && state.i18n.locale) ||
    (typeof window !== "undefined" &&
      window.IdealStudio &&
      window.IdealStudio.i18n &&
      window.IdealStudio.i18n.language) ||
    "en";
  return { sectionId, pluginBoxId, availableScenes, locale };
};

const mapDispatchToProps = (dispatch) => ({
  onApply: (sectionId, proposal) => dispatch(mergeWorkflowProposal(sectionId, proposal)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardSubstep);
// === /Task 9 ===
